import { IRenewCertificateResponse } from '@aca-new/app/pages/reports/shared/interfaces/renew-certificate-response.interface';
import { SERVER_URL } from '@aca-new/app/shared/constants/app.constants';
import { EServiceType } from '@aca-new/app/shared/models/enums/service-type.enum';
import { IHttpResponseBody } from '@aca-new/app/shared/models/interfaces/http-response-body.interface';
import { HttpService } from '@aca-new/app/shared/services/http-services/http.service';
import { AppAuthenticationService } from '@aca-new/app/shared/services/user-services/app-authentication/app-authentication.service';
import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

interface IFoodCertOrderDraftBooking {
  readonly draftId?: string;
}

const FOOD_CERT_API_PREFIX = `${SERVER_URL}/food-certification-services-api`;

@Injectable({
  providedIn: 'root',
})
export class FoodCertificationOrderDraftService {
  public constructor(
    private readonly _httpService: HttpService,
    private readonly _appAuthenticationService: AppAuthenticationService
  ) {}

  /**
   * Creates a food-certification draft from a finished order (renew / book from previous).
   * @param {string} fromOrderId - Finished order id used as recertification source.
   * @param {string} serviceType - Food certification scheme (e.g. brcgs, ggap).
   * @returns {Observable<IRenewCertificateResponse>} Observable with the new draft id.
   */
  public createDraftFromOrder$(fromOrderId: string, serviceType: string): Observable<IRenewCertificateResponse> {
    const normalizedServiceType = serviceType.toLowerCase();

    if (normalizedServiceType === EServiceType.GGAP.toLowerCase()) {
      return this._updateGgapFieldProductsFirstCertification$(fromOrderId).pipe(
        switchMap((): Observable<IRenewCertificateResponse> => this._createOrderDraft$(fromOrderId, normalizedServiceType))
      );
    }

    return this._createOrderDraft$(fromOrderId, normalizedServiceType);
  }

  private _createOrderDraft$(fromOrderId: string, serviceType: string): Observable<IRenewCertificateResponse> {
    const { userId, companyId, parentId } = this._resolveBookingContext();
    const params = new HttpParams().set('userId', userId).set('companyId', companyId).set('parentId', parentId).set('orderId', fromOrderId);
    const url = `${FOOD_CERT_API_PREFIX}/${serviceType}/order-draft`;

    return this._httpService.httpClient.put<IHttpResponseBody<IFoodCertOrderDraftBooking>>(url, null, { params }).pipe(
      map((response: IHttpResponseBody<IFoodCertOrderDraftBooking>): IRenewCertificateResponse => {
        const draftId = response?.content?.draftId?.trim();

        if (!draftId) {
          throw new Error('Food certification order-draft response is missing draftId');
        }

        return { draftId } as IRenewCertificateResponse;
      })
    );
  }

  private _updateGgapFieldProductsFirstCertification$(orderId: string): Observable<void> {
    const url = `${FOOD_CERT_API_PREFIX}/v1.0/client/order/${orderId}/field/products`;

    return this._httpService.httpClient.put<void>(url, null).pipe(map((): void => undefined));
  }

  private _resolveBookingContext(): { companyId: string; parentId: string; userId: string } {
    const user = this._appAuthenticationService.getCurrentUser();

    return {
      userId: user.id,
      companyId: user.company?.id ?? '',
      parentId: user.company?.parentCompanyId ?? '',
    };
  }
}
